import { DigitalUploads, digitalFileKinds } from './DigitalUploads';
import { useFormDraft, useConfirmLeave } from './UnsavedChanges';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, post, useData, useSession, orgID, ApiError } from '@yingbaba/api';
import {
  Button,
  ButtonLink,
  Field,
  Modal,
  Notice,
  ErrorNotice,
  FileAttachment,
} from '@yingbaba/ui';
import {
  money,
  datetime,
  memberCommandKey,
  clearMemberCommand,
  parseMemberMoney,
  beijingToday,
} from '@yingbaba/utils';
import type {
  OrganizationVerificationView,
  DigitalAssetView,
  DigitalPolicy,
  DigitalPublisher,
  DigitalRights,
  DigitalWallet,
  DigitalPayoutAccount,
  DigitalWithdrawal,
  DigitalEarning,
  Page,
} from '@yingbaba/types';
import { digitalStatus, digitalCategory, Pagination, fieldStyle } from './DigitalResources';

type Dialog =
  { kind: 'asset'; value?: DigitalAssetView } | { kind: 'account' } | { kind: 'withdraw' };
const initialRights: DigitalRights = {
  basis: 'original',
  holder_name: '',
  territory: 'CN',
  can_preview: false,
  can_sublicense: false,
  existing_licenses_survive: false,
  third_party_clearance: '',
  likeness_clearance: '',
  music_clearance: '',
  extended_rights: false,
  ai_generated: false,
  ai_provenance: '',
  declaration_version: 'digital-license-v1',
};
export function DigitalSupplier() {
  const session = useSession();
  const org = session.data?.organizations.find((o) => o.id === orgID());
  const canManage = ['owner', 'admin'].includes(org?.role || '');
  const readable = canManage || org?.role === 'finance';
  const qc = useQueryClient();
  const policy = useData<DigitalPolicy>('/digital/policy');
  const publisher = useData<DigitalPublisher>('/digital/publisher', readable);
  const noPublisher = publisher.error instanceof ApiError && publisher.error.status === 404;
  const verification = useData<OrganizationVerificationView>(
    '/organization-verification',
    canManage && noPublisher,
  );
  const verificationReady =
    verification.data?.status === 'verified' &&
    verification.data?.application?.status === 'verified';
  const [page, setPage] = useState(1);
  const [withdrawPage, setWithdrawPage] = useState(1);
  const [earnPage, setEarnPage] = useState(1);
  const mine = useData<Page<DigitalAssetView>>(
    `/digital/my-assets?page=${page}`,
    !!publisher.data && readable,
  );
  const wallet = useData<DigitalWallet>('/digital/wallet', !!publisher.data && readable, 30000);
  const accounts = useData<DigitalPayoutAccount[]>(
    '/digital/payout-accounts',
    !!publisher.data && readable,
  );
  const withdrawals = useData<Page<DigitalWithdrawal>>(
    `/digital/withdrawals?page=${withdrawPage}`,
    !!publisher.data && readable,
    30000,
  );
  const earnings = useData<Page<DigitalEarning>>(
    `/digital/earnings?page=${earnPage}`,
    !!publisher.data && readable,
  );
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [rights, setRights] = useState<DigitalRights>(initialRights);
  const [reuse, setReuse] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const leave = useConfirmLeave();
  const draft = useFormDraft({
    enabled: dialog?.kind === 'asset',
    busy,
    save: persist,
    discard: () => setDialog(null),
  });
  const refresh = () =>
    qc.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes('/digital/'),
    });
  const open = (next: Dialog) => {
    setDialog(next);
    setError(null);
    if (next.kind === 'asset') {
      setRights(next.value ? { ...next.value.rights } : initialRights);
      setReuse(next.value?.files.map((f) => f.id) || []);
    }
  };
  async function run(fn: () => Promise<unknown>, message = '操作已保存') {
    if (pending.current) return false;
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setNotice(message);
      await refresh();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function apply(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await run(
      () =>
        post('/digital/publisher', {
          agreement_version: policy.data?.agreement_version,
          accepted: true,
        }),
      '供稿申请已提交，审核结果会在这里更新。',
    );
  }
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await persist(e.currentTarget);
  }
  async function persist(element: HTMLFormElement): Promise<boolean> {
    if (!dialog || pending.current) return false;
    const form = new FormData(element);
    const str = (k: string) => String(form.get(k) || '').trim();
    const reauthentication = { password: str('password'), mfa_code: str('mfa_code') };
    return run(async () => {
      if (dialog.kind === 'asset') {
        const prices = ['personal', 'business', 'extended'].flatMap((code) => {
          const v = str('price_' + code);
          return v ? [{ code, amount_minor: parseMemberMoney(v) }] : [];
        });
        const payload = {
          asset_id: dialog.value?.asset.id || '',
          expected_version: dialog.value?.asset.revision || 0,
          title: str('title'),
          category: str('category'),
          description: str('description'),
          tags: str('tags'),
          software: str('software'),
          specifications: str('specifications'),
          prices,
          rights: {
            ...rights,
            holder_name: str('holder_name'),
            ai_provenance: str('ai_provenance'),
            sale_ends_at: str('sale_ends_at')
              ? new Date(str('sale_ends_at') + 'T00:00:00+08:00').toISOString()
              : null,
            declaration_version: policy.data?.license_version || 'digital-license-v1',
          },
          reuse_file_ids: reuse,
        };
        await post('/digital/assets', payload);
      } else if (dialog.kind === 'account') {
        const payload = {
          kind: str('kind'),
          bank_name: str('bank_name'),
          legal_name: str('legal_name'),
          number: str('number'),
        };
        const scope = `digital-account:${session.data?.account.id}:${orgID()}`;
        await post('/digital/payout-accounts', {
          ...payload,
          reauthentication,
          idempotency_key: await memberCommandKey(scope, payload),
        });
        clearMemberCommand(scope);
      } else {
        const payload = {
          payout_account_id: str('payout_account_id'),
          amount_minor: parseMemberMoney(str('amount')),
        };
        const scope = `digital-withdraw:${session.data?.account.id}:${orgID()}`;
        await post('/digital/withdrawals', {
          ...payload,
          reauthentication,
          idempotency_key: await memberCommandKey(scope, payload),
        });
        clearMemberCommand(scope);
      }
      draft.clean();
      setDialog(null);
    });
  }
  if (!readable)
    return (
      <Notice>
        当前组织仅所有者、管理员和财务可查看发布者资料。发布、提现和改卡需所有者或管理员权限。
      </Notice>
    );
  return (
    <div>
      <ErrorNotice error={error} />
      {error instanceof ApiError &&
        ['VERIFICATION_REQUIRED', 'ORGANIZATION_VERIFICATION_REQUIRED'].includes(error.code) && (
          <ButtonLink to="/verification" variant="secondary">
            完善主体认证
          </ButtonLink>
        )}
      {notice && <Notice>{notice}</Notice>}
      <ErrorNotice error={!noPublisher ? publisher.error : null} />
      {publisher.isLoading ? (
        <p>正在查询供稿资格…</p>
      ) : noPublisher && (!canManage || !verificationReady) ? (
        <section className="panel form-stack">
          <h2>申请成为数字资源发布者</h2>
          {!canManage ? (
            <Notice>请由当前组织的所有者或管理员完成主体认证并提交供稿申请。</Notice>
          ) : verification.isPending ? (
            <p className="muted">正在确认主体认证状态…</p>
          ) : verification.error ? (
            <>
              <ErrorNotice error={verification.error} />
              <Button variant="secondary" onClick={() => verification.refetch()}>
                重新查询认证状态
              </Button>
            </>
          ) : (
            <>
              <Notice>
                {verification.data?.status === 'pending_review'
                  ? '主体认证正在审核，审核通过后即可申请数字资源供稿。'
                  : '请先完善当前组织的个人或企业主体认证，审核通过后再申请数字资源供稿。'}
              </Notice>
              <div>
                <ButtonLink to="/verification" variant="secondary">
                  {verification.data?.status === 'pending_review' ? '查看认证进度' : '完善主体认证'}
                </ButtonLink>
              </div>
            </>
          )}
        </section>
      ) : noPublisher ? (
        <form onSubmit={apply} className="panel" style={{ padding: 20, ...fieldStyle }}>
          <h2>申请成为数字资源发布者</h2>
          <details>
            <summary>阅读供稿、版权与结算条款</summary>
            <p style={{ whiteSpace: 'pre-wrap' }}>{policy.data?.supplier_terms}</p>
          </details>
          <p>当前组织：{org?.name}。须已完成个人或企业主体认证。</p>
          <p>
            供稿协议：{policy.data?.agreement_version}。平台佣金{' '}
            {(policy.data?.commission_bps || 0) / 100}%；默认结算观察期{' '}
            {policy.data?.settlement_days} 天。仅新协议与新订单适用其冻结规则。
          </p>
          <Notice>
            请确认你拥有作品及所有第三方元素的必要销售、展示与再许可权；平台不会因“原创”勾选免除权属审核。
          </Notice>
          <label>
            <input type="checkbox" required />
            我有权代表当前认证主体，并同意供稿、佣金、结算及版权责任说明。
          </label>
          <Button disabled={busy || !canManage || !policy.data}>提交入驻申请</Button>
        </form>
      ) : (
        publisher.data && (
          <>
            <p>
              发布主体：{publisher.data.legal_name} ·{' '}
              {digitalStatus[publisher.data.status] || publisher.data.status} · 佣金{' '}
              {publisher.data.commission_bps / 100}% · {publisher.data.settlement_days} 天结算观察期
            </p>
            {publisher.data.review_reason && <p>审核说明：{publisher.data.review_reason}</p>}
            <section>
              <h2>我的数字资源</h2>
              {canManage && publisher.data.status === 'active' && (
                <Button onClick={() => open({ kind: 'asset' })}>发布新资源</Button>
              )}
              <ErrorNotice error={mine.error} />
              {mine.data?.items.map((v) => (
                <section
                  key={v.asset.id}
                  className="panel"
                  style={{ padding: 18, marginTop: 12, overflowWrap: 'anywhere' }}
                >
                  <h3>{v.release.title}</h3>
                  <p>
                    {digitalStatus[v.release.status]} · 版本 {v.release.version}
                    {v.asset.rights_hold ? ' · 版权冻结' : ''}
                  </p>
                  <p>{v.release.review_reason}</p>
                  <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                    {canManage && v.release.status !== 'submitted' && !v.asset.rights_hold && (
                      <Button
                        variant="secondary"
                        disabled={busy || uploading[v.asset.id]}
                        onClick={() => open({ kind: 'asset', value: v })}
                      >
                        {v.release.status === 'draft' ? '编辑草稿' : '创建更新版本'}
                      </Button>
                    )}
                    {canManage && v.release.status === 'draft' && (
                      <Button
                        disabled={
                          busy ||
                          uploading[v.asset.id] ||
                          digitalFileKinds.some(
                            (spec) => !v.files.some((f) => f.kind === spec.kind),
                          )
                        }
                        onClick={() =>
                          void run(() =>
                            post(`/digital/my-assets/${v.asset.id}/submit`, {
                              expected_version: v.asset.revision,
                            }),
                          )
                        }
                      >
                        提交审核
                      </Button>
                    )}
                    {canManage && v.asset.status === 'published' && (
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              post(`/digital/my-assets/${v.asset.id}/delist`, {
                                expected_version: v.asset.revision,
                              }),
                            '已停止新销售，合法已购许可与文件版本继续保留。',
                          )
                        }
                      >
                        停止新销售
                      </Button>
                    )}
                  </div>
                  <div className="digital-file-checklist" aria-label="审核材料清单">
                    {digitalFileKinds.map((spec) => (
                      <span
                        key={spec.kind}
                        className={v.files.some((f) => f.kind === spec.kind) ? 'is-complete' : ''}
                      >
                        {spec.label} ·{' '}
                        {v.files.filter((f) => f.kind === spec.kind).length
                          ? `${v.files.filter((f) => f.kind === spec.kind).length} 个文件`
                          : '待上传'}
                      </span>
                    ))}
                  </div>
                  {!!v.files.length && (
                    <div className="digital-supplier-files">
                      {digitalFileKinds.map(
                        (spec) =>
                          v.files.some((f) => f.kind === spec.kind) && (
                            <section key={spec.kind}>
                              <h4>{spec.label}</h4>
                              {v.files
                                .filter((f) => f.kind === spec.kind)
                                .map((f) => (
                                  <div className="digital-supplier-file" key={f.id}>
                                    <FileAttachment
                                      name={f.name}
                                      mime={f.mime}
                                      size={f.size}
                                      previewPath={`/digital/my-assets/${v.asset.id}/files/${f.id}?preview=1`}
                                      downloadPath={`/digital/my-assets/${v.asset.id}/files/${f.id}`}
                                    />
                                    {canManage && v.release.status === 'draft' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={busy || uploading[v.asset.id]}
                                        onClick={() =>
                                          void run(() =>
                                            api(
                                              `/digital/revisions/${v.release.id}/files/${f.id}`,
                                              { method: 'DELETE' },
                                            ),
                                          )
                                        }
                                      >
                                        移出草稿
                                      </Button>
                                    )}
                                  </div>
                                ))}
                            </section>
                          ),
                      )}
                    </div>
                  )}
                  {canManage && v.release.status === 'draft' && !v.asset.rights_hold && (
                    <DigitalUploads
                      key={`${orgID()}:${v.release.id}`}
                      revisionID={v.release.id}
                      fileCount={v.files.length}
                      disabled={busy}
                      onUploaded={refresh}
                      onPending={(value) =>
                        setUploading((previous) =>
                          previous[v.asset.id] === value
                            ? previous
                            : { ...previous, [v.asset.id]: value },
                        )
                      }
                    />
                  )}
                </section>
              ))}
              {mine.data && (
                <Pagination
                  page={page}
                  size={mine.data.page_size}
                  total={mine.data.total}
                  setPage={setPage}
                />
              )}
            </section>
            <section style={{ marginTop: 28 }}>
              <h2>发布者收益</h2>
              <Notice>
                这里是应付收益明细，不是可充值或转账的钱包。提现前须结算成熟、无争议和待追回款；税前申请额与净到账分别记录。
              </Notice>
              <ErrorNotice error={wallet.error} />
              {wallet.data && (
                <div
                  className="panel"
                  style={{
                    padding: 18,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                    gap: 12,
                  }}
                >
                  {[
                    ['pending_minor', '待结算'],
                    ['available_minor', '可提现'],
                    ['held_minor', '争议冻结'],
                    ['reserved_minor', '提现占用'],
                    ['paid_minor', '累计税前支付'],
                    ['debt_minor', '待追回款'],
                    ['recovery_pending_minor', '退款在途出款待核查'],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <p>{label}</p>
                      <strong>{money(Number(wallet.data?.[k as keyof DigitalWallet] || 0))}</strong>
                    </div>
                  ))}
                </div>
              )}
              {canManage && (
                <div className="row" style={{ gap: 10, marginTop: 14 }}>
                  <Button variant="secondary" onClick={() => open({ kind: 'account' })}>
                    新增同名收款账户
                  </Button>
                  <Button
                    disabled={!wallet.data?.can_withdraw}
                    onClick={() => open({ kind: 'withdraw' })}
                  >
                    申请提现
                  </Button>
                </div>
              )}
              {!policy.data?.payouts_enabled && <p>提现当前未开放，账面收益不会因此删除。</p>}
              <ErrorNotice error={accounts.error} />
              {accounts.data?.map((a) => (
                <p key={a.id}>
                  {a.legal_name} · {a.bank_name || '支付宝'} {a.masked_number} ·{' '}
                  {digitalStatus[a.status] || a.status} {a.review_reason}
                </p>
              ))}
            </section>
            <section style={{ marginTop: 24 }}>
              <h2>提现记录</h2>
              <ErrorNotice error={withdrawals.error} />
              {withdrawals.data?.items.map((w) => (
                <div className="list-item" key={w.id}>
                  <div className="grow">
                    <strong>
                      {money(w.amount_minor)} · {digitalStatus[w.status]}
                    </strong>
                    <p>
                      {w.legal_name} {w.masked_account} · 税款 {money(w.tax_minor)} · 净到账{' '}
                      {money(w.net_minor)}
                    </p>
                    <p className="small muted">{w.merchant_transfer_no}</p>
                    <p>{w.reason}</p>
                  </div>
                  {canManage && ['requested', 'approved'].includes(w.status) && (
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void run(() => post(`/digital/withdrawals/${w.id}/cancel`))}
                    >
                      撤回申请
                    </Button>
                  )}
                </div>
              ))}
              {withdrawals.data && (
                <Pagination
                  page={withdrawPage}
                  size={withdrawals.data.page_size}
                  total={withdrawals.data.total}
                  setPage={setWithdrawPage}
                />
              )}
            </section>
            <section style={{ marginTop: 24 }}>
              <h2>逐笔销售收益</h2>
              <ErrorNotice error={earnings.error} />
              {earnings.data?.items.map((v) => (
                <div className="list-item" key={v.id}>
                  <div className="grow">
                    <strong>
                      税前收益 {money(v.amount_minor)}
                      {v.refunded ? ' · 已退款反冲' : ''}
                    </strong>
                    <p className="small muted">订单 {v.order_id}</p>
                    <p>
                      结算观察期至 {datetime(v.available_at)} · 已支付 {money(v.paid_minor)} ·
                      已反冲 {money(v.reversed_minor)}
                    </p>
                  </div>
                </div>
              ))}
              {earnings.data && (
                <Pagination
                  page={earnPage}
                  size={earnings.data.page_size}
                  total={earnings.data.total}
                  setPage={setEarnPage}
                />
              )}
            </section>
          </>
        )
      )}
      {dialog && (
        <Modal
          open
          busy={busy}
          title={
            dialog.kind === 'asset'
              ? '数字资源草稿'
              : dialog.kind === 'account'
                ? '同名收款账户'
                : '申请提现'
          }
          onOpenChange={(o) => {
            if (!o && !busy) leave(() => setDialog(null));
          }}
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => leave(() => setDialog(null))}
              >
                取消
              </Button>
              <Button type="submit" form="digital-supplier-dialog" loading={busy}>
                {dialog.kind === 'asset'
                  ? '保存草稿'
                  : dialog.kind === 'account'
                    ? '保存收款账户'
                    : '提交提现申请'}
              </Button>
            </>
          }
        >
          <form
            id="digital-supplier-dialog"
            ref={draft.ref}
            onChange={draft.onChange}
            onSubmit={submit}
            style={fieldStyle}
          >
            <fieldset className="form-draft-fields" disabled={!!busy}>
              {dialog.kind === 'asset' ? (
                <>
                  <label className="field">
                    资源标题
                    <input
                      className="input"
                      name="title"
                      required
                      minLength={2}
                      maxLength={120}
                      defaultValue={dialog.value?.release.title}
                    />
                  </label>
                  <label className="field">
                    类别
                    <select
                      name="category"
                      defaultValue={dialog.value?.release.category || 'video'}
                    >
                      {Object.entries(digitalCategory).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    内容与交付说明
                    <textarea
                      name="description"
                      required
                      maxLength={10000}
                      defaultValue={dialog.value?.release.description}
                    />
                  </label>
                  <label className="field">
                    关键词
                    <input
                      className="input"
                      name="tags"
                      maxLength={500}
                      defaultValue={dialog.value?.release.tags}
                    />
                  </label>
                  <label className="field">
                    技术规格
                    <input
                      className="input"
                      name="specifications"
                      maxLength={1000}
                      defaultValue={dialog.value?.release.specifications}
                      placeholder="分辨率、时长、帧率、透明通道等"
                    />
                  </label>
                  <label className="field">
                    软件版本、插件、字体依赖
                    <input
                      className="input"
                      name="software"
                      maxLength={160}
                      defaultValue={dialog.value?.release.software}
                    />
                  </label>
                  <label className="field">
                    实际权利人
                    <input
                      className="input"
                      name="holder_name"
                      required
                      maxLength={120}
                      defaultValue={dialog.value?.rights.holder_name || publisher.data?.legal_name}
                    />
                  </label>
                  <label className="field">
                    权属来源
                    <select
                      value={rights.basis}
                      onChange={(e) => setRights({ ...rights, basis: e.target.value })}
                    >
                      <option value="original">原创</option>
                      <option value="authorized">已获完整再销售/再许可授权</option>
                    </select>
                  </label>
                  <label className="field">
                    许可地域
                    <select
                      value={rights.territory}
                      onChange={(e) => setRights({ ...rights, territory: e.target.value })}
                    >
                      <option value="CN">中国大陆</option>
                      <option value="worldwide">全球</option>
                    </select>
                  </label>
                  <label className="field">
                    新销售授权截止日（北京时间零时，不填表示无独立截止日）
                    <input
                      className="input"
                      name="sale_ends_at"
                      type="date"
                      defaultValue={
                        dialog.value?.rights.sale_ends_at
                          ? beijingToday(new Date(dialog.value.rights.sale_ends_at))
                          : undefined
                      }
                    />
                  </label>
                  {[
                    ['third_party_clearance', '其他第三方作品/字体/商标'],
                    ['likeness_clearance', '肖像与声音'],
                    ['music_clearance', '音乐词曲/录音/表演'],
                  ].map(([k, label]) => (
                    <label className="field" key={k}>
                      {label}
                      <select
                        value={String(rights[k as keyof DigitalRights] || '')}
                        onChange={(e) => setRights({ ...rights, [k]: e.target.value })}
                      >
                        <option value="">请选择实际权利状态</option>
                        <option value="none">不涉及</option>
                        <option value="cleared">已取得覆盖本次再许可的证明</option>
                      </select>
                    </label>
                  ))}
                  {[
                    ['can_preview', '允许平台托管、制作必要预览及展示'],
                    ['can_sublicense', '允许平台在所售授权范围内向买家再许可'],
                    ['existing_licenses_survive', '确认已售具名项目许可可按约存续'],
                    ['extended_rights', '持有扩展授权所需的广播/院线及商品化权利'],
                    ['ai_generated', '此资源包含 AIGC 生成内容'],
                  ].map(([k, label]) => (
                    <label key={k}>
                      <input
                        type="checkbox"
                        checked={rights[k as keyof DigitalRights] === true}
                        onChange={(e) => setRights({ ...rights, [k]: e.target.checked })}
                      />
                      {label}
                    </label>
                  ))}
                  {rights.ai_generated && (
                    <label className="field">
                      AIGC 工具、人工贡献及权利依据
                      <textarea
                        name="ai_provenance"
                        minLength={10}
                        maxLength={2000}
                        required
                        defaultValue={rights.ai_provenance}
                      />
                    </label>
                  )}
                  {['personal', 'business', 'extended'].map((code) => (
                    <label className="field" key={code}>
                      {policy.data?.licenses.find((l) => l.code === code)?.name || code}{' '}
                      价格（元，不填则不提供）
                      <input
                        className="input"
                        name={`price_${code}`}
                        inputMode="decimal"
                        defaultValue={
                          dialog.value?.prices.find((p) => p.code === code)
                            ? String(
                                dialog.value.prices.find((p) => p.code === code)!.amount_minor /
                                  100,
                              )
                            : ''
                        }
                      />
                    </label>
                  ))}
                  {!!dialog.value?.files.length && dialog.value.release.status !== 'draft' && (
                    <fieldset>
                      <legend>新版本保留的既有文件</legend>
                      {dialog.value.files.map((f) => (
                        <label key={f.id} style={{ display: 'block' }}>
                          <input
                            type="checkbox"
                            checked={reuse.includes(f.id)}
                            onChange={(e) =>
                              setReuse(
                                e.target.checked
                                  ? [...reuse, f.id]
                                  : reuse.filter((id) => id !== f.id),
                              )
                            }
                          />
                          {f.name}（{f.kind}）
                        </label>
                      ))}
                    </fieldset>
                  )}
                  <Notice>
                    先保存草稿，再上传原件、带水印预览及权属证明；送审后内容冻结。旧买家的已购文件不被替换。
                  </Notice>
                </>
              ) : (
                <>
                  {dialog.kind === 'account' ? (
                    <>
                      <label className="field">
                        收款类型
                        <select name="kind">
                          <option value="bank">
                            {publisher.data?.subject_type === 'enterprise'
                              ? '同名对公银行账户'
                              : '本人银行账户'}
                          </option>
                          {publisher.data?.subject_type !== 'enterprise' && (
                            <option value="alipay">本人支付宝</option>
                          )}
                        </select>
                      </label>
                      <label className="field">
                        认证主体同名收款人
                        <input
                          className="input"
                          name="legal_name"
                          required
                          defaultValue={publisher.data?.legal_name}
                          maxLength={120}
                        />
                      </label>
                      <label className="field">
                        开户行
                        <input className="input" name="bank_name" maxLength={120} />
                      </label>
                      <label className="field">
                        完整收款账号
                        <input
                          className="input"
                          name="number"
                          required
                          autoComplete="off"
                          minLength={6}
                          maxLength={128}
                        />
                      </label>
                      <Notice>
                        个人仅限本人、企业仅限同名企业账户。完整号码将加密保存并单独审核。
                      </Notice>
                    </>
                  ) : (
                    <>
                      <label className="field">
                        已审核账户
                        <select name="payout_account_id" required>
                          <option value="">请选择</option>
                          {accounts.data
                            ?.filter((a) => a.status === 'approved')
                            .map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.legal_name} {a.masked_number}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label className="field">
                        提现税前金额（元）
                        <input className="input" name="amount" required inputMode="decimal" />
                      </label>
                      <Notice>
                        申请将占用可提现收益。税款依据实际核验，不套用固定税率；进入处理中后不能因超时取消或重复打款。
                      </Notice>
                    </>
                  )}
                  <label className="field">
                    当前登录密码
                    <input
                      className="input"
                      name="password"
                      type="password"
                      required
                      autoComplete="current-password"
                    />
                  </label>
                  <label className="field">
                    双因素验证码/恢复码（已开启时必填）
                    <input
                      className="input"
                      name="mfa_code"
                      autoComplete="one-time-code"
                      maxLength={32}
                    />
                  </label>
                </>
              )}
              <ErrorNotice error={error} />
            </fieldset>
          </form>
        </Modal>
      )}
    </div>
  );
}
